<template>
  <nav class="admin-menu" aria-label="منوی مدیریت">
    <div class="admin-menu__header">
      <div><span class="admin-menu__eyebrow">فضای کاری</span><strong class="admin-menu__title">مدیریت مهر</strong></div>
      <span class="admin-menu__badge" aria-hidden="true">م</span>
    </div>
    <div class="admin-menu-search" role="search">
      <i class="pi pi-search admin-menu-search__icon" aria-hidden="true"></i>
      <input
        v-model="searchQuery"
        type="search"
        placeholder="جست‌وجو در منو..."
        aria-label="جست‌وجو در منوی مدیریت"
        autocomplete="off"
        @keydown.esc="clearSearch"
      />
      <button v-if="searchQuery" type="button" aria-label="پاک کردن جست‌وجو" @click="clearSearch">
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>
    <div v-if="isSearching" class="admin-menu__search-meta" aria-live="polite">
      <span>{{ resultCount ? `${toPersianNumber(resultCount)} نتیجه` : 'نتیجه‌ای پیدا نشد' }}</span>
      <button v-if="resultCount" type="button" @click="clearSearch">نمایش همه</button>
    </div>
    <ul v-if="filteredModel.length" class="layout-menu">
      <template v-for="(item, i) in filteredModel" :key="item.label || i">
        <app-menu-item v-if="!item.separator" :item="item" :index="i" :expand-all="isSearching"></app-menu-item>
        <li v-else class="menu-separator"></li>
      </template>
    </ul>
    <div v-else class="admin-menu-empty">
      <span class="admin-menu-empty__icon"><i class="pi pi-search" aria-hidden="true"></i></span>
      <strong>چیزی پیدا نکردیم</strong>
      <p>عنوان بخش موردنظرتان را با عبارت دیگری جست‌وجو کنید.</p>
      <button type="button" @click="clearSearch">پاک کردن جست‌وجو</button>
    </div>
  </nav>
</template>

<script setup>
import { computed, nextTick, ref } from 'vue';

import AppMenuItem from './AppMenuItem.vue';
const userPermissions = JSON.parse(localStorage.getItem('user_permissions')) || [];
const rawModel = ref([
  {
    label: '',
    items: [
      {
        label: 'داشبورد',
        icon: 'house',
        to: '/',
        permission: 'dashboard'
      }
    ]
  },
  {
    label: '',
    icon: 'gear',
    items: [
      {
        label: 'سفارشات',
        icon: 'cart-shopping',
        items: [
          {
            label: 'لیست سفارشات',
            icon: 'clipboard-list',
            to: { name: 'Orders' },
            permission: 'orders'
          },
          {
            label: 'سفارشات ویژه',
            icon: 'star',
            to: { name: 'SpecialOrder' },
            permission: 'special-orders'
          },
          {
            label: 'سفارشات مرجوعی',
            icon: 'rotate-left',
            to: { name: 'ReturningItems' },
            permission: 'returning-items'
          }
        ]
      },
      {
        label: 'مدیریت محصولات',
        icon: 'gem',
        items: [
          {
            label: 'لیست محصولات',
            icon: 'tags',
            to: { name: 'Product' },
            permission: 'product'
          },
          {
            label: 'جنس محصول - مظنه لحظه ای',
            icon: 'warehouse',
            to: { name: 'ProductType' },
            permission: 'product-type'
          },

          {
            label: 'محصولات تخفیف دار',
            icon: 'percent',
            to: { name: 'DiscountProductList' },
            permission: 'discount-product-items'
          },
          {
            label: 'بن های تخفیف',
            icon: 'percent',
            to: { name: 'Discounts' },
            permission: 'discounts'
          },
          {
            label: 'تنوع محصولات و موجودی',
            icon: 'gem',
            to: { name: 'ProductItems' },
            permission: 'product-items'
          },
          {
            label: 'تاریخچه محصولات و موجودی',
            icon: 'clock-rotate-left',
            to: { name: 'InventoryHistory' },
            permission: 'product-items'
          },
          {
            label: 'انبار مرکزی و همگام‌سازی',
            hideForVariantWorkspace: true,
            icon: 'warehouse',
            to: { name: 'CentralInventory' },
            permissionsAny: ['product-items', 'marketplace']
          },
          {
            label: 'گزارش انبار',
            icon: 'gem',
            to: { name: 'Report' },
            permission: 'report'
          },
          {
            label: 'تجهیزات',
            icon: 'cubes',
            to: { name: 'Equipment' },
            permission: 'equipment'
          },
          {
            label: 'فرمول اجرت',
            icon: 'calculator',
            to: { name: 'Wages' },
            permission: 'wages'
          },
          {
            label: 'دسته بندی محصولات',
            icon: 'folder-tree',
            to: { name: 'ProductCategory' },
            permission: 'product-category'
          },

          {
            label: 'کارت های هدیه',
            icon: 'gift',
            to: { name: 'GiftCards' },
            permission: 'gift-cards'
          },
          {
            label: 'رنگ ها',
            icon: 'droplet',
            to: { name: 'Colors' },
            permission: 'colors'
          },
          {
            label: 'تنوع مشتریان',
            icon: 'person-half-dress',
            to: { name: 'ProductUsage' },
            permission: 'product-usage'
          },

          {
            label: 'عیار ها',
            icon: 'scale-balanced',
            to: { name: 'Karat' },
            permission: 'karat'
          },
          {
            label: 'برند ها',
            icon: 'copyright',
            to: { name: 'Brands' },
            permission: 'brands'
          },
          {
            label: 'گارانتی ها',
            icon: 'award',
            to: { name: 'Warranty' },
            permission: 'warranty'
          }
        ]
      },
      {
        label: 'مارکت‌پلیس',
        icon: 'store',
        items: [
          {
            label: 'کانال‌ها',
            icon: 'store',
            to: { name: 'MarketplaceChannels' },
            permission: 'marketplace'
          },
          {
            label: 'نگاشت کالا',
            icon: 'link',
            to: { name: 'MarketplaceMappings' },
            permission: 'marketplace'
          },
          {
            label: 'سفارشات مارکت‌پلیس',
            icon: 'cart-shopping',
            to: { name: 'MarketplaceOrders' },
            permission: 'marketplace-orders'
          },
          {
            label: 'محموله‌ها',
            icon: 'box',
            to: { name: 'MarketplacePackages' },
            permission: 'marketplace'
          }
        ]
      },
      {
        label: 'باشگاه مشتریان و اصالت',
        icon: 'shield-halved',
        items: [
          {
            label: 'درخواست‌های اصالت',
            icon: 'shield',
            to: { name: 'AuthenticityRequests' },
            permission: 'authenticity-request-list'
          },
          {
            label: 'پلتفرم‌های خرید',
            icon: 'store',
            to: { name: 'AuthenticityPlatforms' },
            permission: 'authenticity-platform-manage'
          },
          {
            label: 'تنظیمات اصالت',
            icon: 'gear',
            to: { name: 'AuthenticitySettings' },
            permission: 'authenticity-setting-manage'
          },
          {
            label: 'کمپین‌های پیامکی',
            icon: 'bullhorn',
            to: { name: 'AuthenticityCampaigns' },
            permission: 'authenticity-campaign-view'
          },
          {
            label: 'دفتر امتیاز باشگاه',
            icon: 'coins',
            to: { name: 'CustomerClubPoints' },
            permission: 'club-points-list'
          },
          {
            label: 'تنظیمات مهربن',
            icon: 'gift',
            to: { name: 'MehrBonSetting' },
            permission: 'mehr-bon-setting'
          }
        ]
      },
      {
        label: 'مدیریت محتوا',
        icon: 'newspaper',
        items: [
          {
            label: 'لیست مقالات',
            icon: 'radio',
            to: { name: 'Article', query: { type: 'article' } },
            permission: 'article'
          },
          {
            label: 'لیست اخبار',
            icon: 'file-lines',
            to: { name: 'NewsArticle', query: { type: 'news' } },
            permission: 'news-article'
          },
          {
            label: 'لیست صفحات',
            icon: 'newspaper',
            to: { name: 'PageArticle', query: { type: 'page' } },
            permission: 'page-article'
          },
          {
            label: 'دسته بندی مقالات',
            icon: 'folder-tree',
            to: { name: 'ArticleCategory', query: { type: 'article' } },
            permission: 'article-category'
          },
          {
            label: 'دسته بندی اخبار',
            icon: 'folder-tree',
            to: { name: 'ArticleCategory', query: { type: 'news' } },
            permission: 'news-category'
          },
          {
            label: 'دسته بندی صفحات',
            icon: 'folder-tree',
            to: { name: 'ArticleCategory', query: { type: 'page' } },
            permission: 'page-category'
          }
        ]
      },
      {
        label: 'تنظیمات پایه',
        icon: 'gear',
        items: [
          {
            label: 'تنظیمات اصلی',
            icon: 'clock-rotate-left',
            to: '/settings',
            permission: 'settings'
          },
          {
            label: 'لیست مشتریان',
            icon: 'user-shield',
            to: '/customers',
            permission: 'customers'
          },
          {
            label: 'تاریخچه بروزرسانی',
            icon: 'gear',
            to: '/price-history',
            permission: 'price-history'
          },
          {
            label: 'مدیران',
            icon: 'key',
            to: '/admins',
            permission: 'admins'
          },
          {
            label: 'سطوح دسترسی',
            icon: 'users',
            to: '/roles',
            permission: 'roles'
          },
          {
            label: 'پیام انبوه',
            icon: 'bullhorn',
            to: { name: 'Notification' },
            permission: 'notification'
          },
          {
            label: 'کشور ها',
            icon: 'earth-asia',
            to: '/countries',
            permission: 'countries'
          },
          {
            label: 'شهر ها',
            icon: 'city',
            to: '/cities',
            permission: 'cities'
          },
          {
            label: 'روز های تعطیل',
            icon: 'calendar',
            to: { name: 'OffDays' },
            permission: 'off-days'
          },
          {
            label: 'اسلایدر ها',
            icon: 'images',
            to: { name: 'Slider' },
            permission: 'slider'
          },
          {
            label: 'بنر ها',
            icon: 'images',
            to: { name: 'Banner' },
            permission: 'banner'
          },
          {
            label: 'شیوه های حمل',
            icon: 'money-check-dollar',
            to: { name: 'Shipment' },
            permission: 'shipment'
          },
          {
            label: 'شیوه های پرداخت',
            icon: 'truck-pickup',
            to: { name: 'PaymentMethod' },
            permission: 'payment-methods'
          }
        ]
      },
      {
        label: 'مدیریت تیکت',
        icon: 'life-ring',
        items: [
          {
            label: 'مدیریت تیکت',
            icon: 'ticket',
            to: { name: 'Ticket' },
            permission: 'ticket'
          },
          {
            label: 'مدیریت نظرات',
            icon: 'comments',
            to: { name: 'Comments' },
            permission: 'comments'
          },
          {
            label: 'مدیریت تماس ها',
            icon: 'phone',
            to: { name: 'Contacts' },
            permission: 'contacts'
          },
          {
            label: 'دپارتمان تیکت ها',
            icon: 'building-user',
            to: { name: 'TicketDepartment' },
            permission: 'ticket-department'
          }
        ]
      }
    ]
  }
]);
function filterMenuByPermissions(menuRef, userPermissions) {
  const menu = Array.isArray(menuRef) ? menuRef : menuRef.value;
  if (!Array.isArray(menu)) {
    console.error('⛔️ menu is not an array:', menu);
    return [];
  }

  return menu
    .map((item) => {
      if (item.hideForVariantWorkspace && userPermissions.includes('product-items')) return null;
      if (item.items) {
        const filteredChildren = filterMenuByPermissions(item.items, userPermissions);
        if (filteredChildren.length > 0) {
          return {
            ...item,
            items: filteredChildren
          };
        }
      }

      const allowed = item.permissionsAny
        ? item.permissionsAny.some((permission) => userPermissions.includes(permission))
        : !item.permission || userPermissions.includes(item.permission);
      if (!item.items && allowed) {
        return item;
      }

      return null;
    })
    .filter(Boolean);
}

// فیلتر نهایی منو
const model = ref(filterMenuByPermissions(rawModel, userPermissions));
const searchQuery = ref('');
const normalizeText = (value = '') =>
  String(value)
    .trim()
    .toLocaleLowerCase('fa')
    .replace(/[يى]/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/\u200c/g, ' ')
    .replace(/\s+/g, ' ');
const filterMenuByQuery = (items, query) =>
  items.reduce((matches, item) => {
    if (item.separator) return matches;
    const labelMatches = normalizeText(item.label).includes(query);
    const childMatches = item.items ? filterMenuByQuery(item.items, query) : [];
    if (labelMatches) matches.push(item);
    else if (childMatches.length) matches.push({ ...item, items: childMatches });
    return matches;
  }, []);
const normalizedQuery = computed(() => normalizeText(searchQuery.value));
const isSearching = computed(() => normalizedQuery.value.length > 0);
const filteredModel = computed(() =>
  isSearching.value ? filterMenuByQuery(model.value, normalizedQuery.value) : model.value
);
const countLeaves = (items) => items.reduce((total, item) => total + (item.items ? countLeaves(item.items) : 1), 0);
const resultCount = computed(() => (isSearching.value ? countLeaves(filteredModel.value) : 0));
const toPersianNumber = (value) => String(value).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[digit]);
const clearSearch = () => {
  searchQuery.value = '';
  nextTick(() => document.querySelector('.admin-menu-search input')?.focus());
};
</script>

<style lang="scss" scoped></style>
